/**
 * DraftContext
 *
 * Central draft system for the Draft/Live mode.
 * Stores all pending admin changes (content, sections, appearance) in the
 * database via the SiteDraft API. In-memory state acts as a write-through
 * cache, auto-saved to the backend on a debounced timer.
 *
 * Players never see draft data — it only surfaces when isAdmin or isPreviewMode.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getConfig } from '../config/environment';
import { useIsAdmin } from '../hooks/useIsAdmin';
import api, { apiRoutes } from '../api';
import DEFAULT_SECTIONS from '../config/defaultSections';
import { normalizeSectionsForSave } from '../utils/pageSections';
import {
  getAuthorizedContentAdminToken,
  getAdminRefreshToken,
} from '../utils/adminToken';

const DraftContext = createContext(null);

/** Count leaf values in an object (nested 2 levels max) */
function countChanges(obj) {
  if (!obj || typeof obj !== 'object') return 0;
  let count = 0;
  for (const v of Object.values(obj)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      count += Object.keys(v).length;
    } else {
      count += 1;
    }
  }
  return count;
}

/**
 * Convert frontend snake_case sections → API camelCase for backend persistence.
 * The backend PublishSiteDraftHandler deserializes into PageSectionDto (PascalCase
 * properties) which Newtonsoft matches case-insensitively against camelCase keys.
 */
function sectionsToApiFormat(sectionsByPage) {
  const converted = {};
  for (const [pageName, sections] of Object.entries(sectionsByPage)) {
    const normalizedSections = normalizeSectionsForSave(sections || [], DEFAULT_SECTIONS[pageName] || []);
    converted[pageName] = normalizedSections.map(s => ({
      id: s.id == null ? '' : String(s.id),
      pageName: s.page_name || s.pageName || pageName,
      sectionType: s.section_type || s.sectionType || '',
      sectionOrder: s.section_order ?? s.sectionOrder ?? 0,
      isVisible: s.is_visible ?? s.isVisible ?? true,
      config: s.config || {},
    }));
  }
  return converted;
}

/**
 * Convert API camelCase sections → frontend snake_case for in-memory use.
 * Handles both camelCase (new drafts) and snake_case (legacy drafts) gracefully.
 */
function sectionsFromApiFormat(sectionsByPage) {
  if (!sectionsByPage || typeof sectionsByPage !== 'object') return {};
  const converted = {};
  for (const [pageName, sections] of Object.entries(sectionsByPage)) {
    converted[pageName] = (sections || []).map(s => ({
      id: s.id ?? s.Id,
      page_name: s.page_name || s.pageName || pageName,
      section_type: s.section_type || s.sectionType || '',
      section_order: s.section_order ?? s.sectionOrder ?? 0,
      is_visible: s.is_visible ?? s.isVisible ?? true,
      config: s.config || {},
    }));
  }
  return converted;
}

/**
 * Build auth headers for draft API calls.
 * NEVER falls back to member access_token — admin endpoints require admin auth.
 * Returns empty headers if no valid admin token exists (the API interceptor
 * will attempt a refresh on the resulting 401).
 */
function getAdminAuthHeaders() {
  const { tlfId } = getConfig();
  const token = getAuthorizedContentAdminToken(tlfId);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function DraftProvider({ children }) {
  const { tlfId } = getConfig();
  const { isAdmin } = useIsAdmin();
  const orgId = tlfId || 'unknown';

  // Draft data — in-memory, backed by database via API
  const [draftContent, setDraftContent] = useState({});
  const [draftSections, setDraftSections] = useState({});
  const [draftAppearance, setDraftAppearance] = useState({});
  const [draftVersion, setDraftVersion] = useState(0);
  const [draftMeta, setDraftMeta] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Synchronous refs for draft state to avoid stale closure races during rapid saves
  const draftContentRef = useRef(draftContent);
  draftContentRef.current = draftContent;
  const draftSectionsRef = useRef(draftSections);
  draftSectionsRef.current = draftSections;
  const draftAppearanceRef = useRef(draftAppearance);
  draftAppearanceRef.current = draftAppearance;

  // Preview mode — toggled by PreviewPage on mount
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Publish state
  const [publishStatus, setPublishStatus] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  // Track whether there are unsaved in-memory changes not yet persisted to API
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveError, setLastSaveError] = useState(null);

  // Guards: prevent auto-save from re-creating a draft during/after discard or publish
  const isDiscardingRef = useRef(false);
  const isPublishingRef = useRef(false);

  // Synchronous save-in-progress guard (useRef, not state, so concurrent callers
  // see the updated value immediately — avoids the stale-closure race where two
  // calls both read isSaving===false before the first setIsSaving(true) renders).
  const isSavingRef = useRef(false);
  // Stores the currently in-flight save promise so a second caller can await
  // the same request instead of firing a duplicate that would cause a version conflict.
  const saveInProgressPromiseRef = useRef(null);

  // Keep a ref to the latest draft version so publish always uses the freshest value
  const draftVersionRef = useRef(draftVersion);
  draftVersionRef.current = draftVersion;

  // Broadcast draft appearance changes so sibling/ancestor contexts
  // (LayoutStyle, ColorTheme, CountdownStyle) can sync without being below Draft in the tree.
  // Fires on every draftAppearance change including initial API load.
  useEffect(() => {
    if (!isAdmin) return;
    try {
      window.dispatchEvent(new CustomEvent('appearanceDraftSync', {
        detail: { appearance: draftAppearance || {} },
      }));
    } catch {}
  }, [draftAppearance, isAdmin]);

  // Load draft from API on mount / org change
  useEffect(() => {
    if (!isAdmin) {
      setIsLoaded(true);
      return;
    }
    let cancelled = false;

    const loadDraft = async () => {
      try {
        const res = await api.get(apiRoutes.SiteDraft, {
          headers: getAdminAuthHeaders(),
        });
        if (cancelled) return;

        const draft = res?.data?.payload?.draft;
        if (draft) {
          const parsedContent = draft.contentJson ? JSON.parse(draft.contentJson) : {};
          const parsedSections = draft.sectionsJson ? sectionsFromApiFormat(JSON.parse(draft.sectionsJson)) : {};
          const parsedAppearance = draft.appearanceJson ? JSON.parse(draft.appearanceJson) : {};
          draftContentRef.current = parsedContent;
          draftSectionsRef.current = parsedSections;
          draftAppearanceRef.current = parsedAppearance;
          setDraftContent(parsedContent);
          setDraftSections(parsedSections);
          setDraftAppearance(parsedAppearance);
          if (parsedAppearance?.defaultPageVisibility) {
            try {
              localStorage.setItem('site_default_page_visibility', JSON.stringify(parsedAppearance.defaultPageVisibility));
            } catch {}
          }
          if (parsedAppearance?.how_to_playPageLayout) {
            try {
              localStorage.setItem('site_how_to_play_page_layout', parsedAppearance.how_to_playPageLayout);
            } catch {}
          }
          if (parsedAppearance?.aboutPageLayout) {
            try {
              localStorage.setItem('site_about_page_layout', parsedAppearance.aboutPageLayout);
            } catch {}
          }
          if (parsedAppearance?.winnersPageLayout) {
            try {
              localStorage.setItem('site_winners_page_layout', parsedAppearance.winnersPageLayout);
            } catch {}
          }
          if (parsedAppearance?.drawsPageLayout) {
            try {
              localStorage.setItem('site_draws_page_layout', parsedAppearance.drawsPageLayout);
            } catch {}
          }
          setDraftVersion(draft.version || 0);
          setDraftMeta({
            createdAt: draft.createdAt,
            updatedAt: draft.updatedAt,
            createdByUserId: draft.createdByUserId,
            updatedByUserId: draft.updatedByUserId,
          });
        } else {
          draftContentRef.current = {};
          draftSectionsRef.current = {};
          draftAppearanceRef.current = {};
          setDraftContent({});
          setDraftSections({});
          setDraftAppearance({});
          setDraftVersion(0);
          setDraftMeta(null);
        }
      } catch (err) {
        console.warn('[DraftContext] Failed to load draft from API:', err.message);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };

    loadDraft();
    return () => { cancelled = true; };
  }, [orgId, isAdmin]);

  // Re-fetch draft when tab regains focus (cross-tab sync after publish in preview)
  const lastFetchRef = useRef(0);
  useEffect(() => {
    if (!isAdmin) return;

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFetchRef.current < 5000) return; // skip if fetched <5s ago
      lastFetchRef.current = now;

      (async () => {
        try {
          const res = await api.get(apiRoutes.SiteDraft, { headers: getAdminAuthHeaders() });
          const draft = res?.data?.payload?.draft;
          if (draft) {
            const parsedContent = draft.contentJson ? JSON.parse(draft.contentJson) : {};
            const parsedSections = draft.sectionsJson ? sectionsFromApiFormat(JSON.parse(draft.sectionsJson)) : {};
            const parsedAppearance = draft.appearanceJson ? JSON.parse(draft.appearanceJson) : {};
            draftContentRef.current = parsedContent;
            draftSectionsRef.current = parsedSections;
            draftAppearanceRef.current = parsedAppearance;
            setDraftContent(parsedContent);
            setDraftSections(parsedSections);
            setDraftAppearance(parsedAppearance);
            setDraftVersion(draft.version || 0);
            setDraftMeta({
              createdAt: draft.createdAt, updatedAt: draft.updatedAt,
              createdByUserId: draft.createdByUserId, updatedByUserId: draft.updatedByUserId,
            });
          } else {
            draftContentRef.current = {};
            draftSectionsRef.current = {};
            draftAppearanceRef.current = {};
            setDraftContent({});
            setDraftSections({});
            setDraftAppearance({});
            setDraftVersion(0);
            setDraftMeta(null);
          }
          setIsDirty(false);
        } catch (err) {
          console.debug('[DraftContext] visibilitychange refetch failed:', err.message);
        }
      })();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAdmin]);

  // Derived state
  const contentChangeCount = countChanges(draftContent);
  const sectionChangeCount = Object.keys(draftSections).length;
  const appearanceChangeCount = countChanges(draftAppearance);
  const changeCount = contentChangeCount + sectionChangeCount + appearanceChangeCount;
  const hasDraft = changeCount > 0;

  const shouldShowDraft = isAdmin || isPreviewMode;

  // --- Content draft API ---
  const updateDraftContent = useCallback((sectionName, value, lang) => {
    setDraftContent(prev => {
      const next = { ...prev };
      if (!next[lang]) next[lang] = {};
      next[lang] = { ...next[lang], [sectionName]: value };
      draftContentRef.current = next;
      return next;
    });
    setIsDirty(true);
  }, []);

  const getDraftContent = useCallback((sectionName, lang) => {
    return draftContentRef.current?.[lang]?.[sectionName] ?? draftContent[lang]?.[sectionName];
  }, [draftContent]);

  // --- Sections draft API ---
  const updateDraftSections = useCallback((pageName, sections) => {
    setDraftSections(prev => {
      const next = {
        ...prev,
        [pageName]: sections,
      };
      draftSectionsRef.current = next;
      return next;
    });
    setIsDirty(true);
    
    // Broadcast section draft change so pages can re-render
    if (isAdmin) {
      try {
        window.dispatchEvent(new CustomEvent('sectionsDraftSync', {
          detail: { pageName, sections },
        }));
      } catch {}
    }
  }, [isAdmin]);

  const getDraftSections = useCallback((pageName) => {
    return draftSectionsRef.current?.[pageName] ?? draftSections[pageName] ?? null;
  }, [draftSections]);

  // --- Appearance draft API ---
  const updateDraftAppearance = useCallback((field, value) => {
    setDraftAppearance(prev => {
      const next = {
        ...prev,
        [field]: value,
      };
      draftAppearanceRef.current = next;
      return next;
    });
    setIsDirty(true);
  }, []);

  const getDraftAppearance = useCallback((field) => {
    return draftAppearanceRef.current?.[field] ?? draftAppearance[field];
  }, [draftAppearance]);

  // --- Persist to API ---
  // Returns { success: boolean, version?: number, reason?: string }
  // - success=true with version when draft was saved to the API
  // - success=false with reason='already-saving' when another save is in flight
  // - success=false with reason='discarding'/'publishing' when a lifecycle action is active
  // - success=false with reason='save-failed' when the API call fails
  const saveDraft = useCallback(async () => {
    // Clear any pending auto-save timer to prevent races during this manual save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    // Use the ref guard (synchronous) instead of isSaving state (async) so
    // concurrent callers see the lock immediately without waiting for a render.
    if (isSavingRef.current) {
      // Return the in-flight promise so the caller awaits the same request
      // rather than firing a duplicate that would cause a version conflict.
      return saveInProgressPromiseRef.current ?? { success: false, reason: 'already-saving' };
    }
    if (isDiscardingRef.current) return { success: false, reason: 'discarding' };
    if (isPublishingRef.current) return { success: false, reason: 'publishing' };

    isSavingRef.current = true;
    setIsSaving(true);
    const savePromise = (async () => {
    try {
      const currentContent = draftContentRef.current || draftContent;
      const currentSections = draftSectionsRef.current || draftSections;
      const currentAppearance = draftAppearanceRef.current || draftAppearance;

      const payload = {
        contentJson: Object.keys(currentContent).length > 0 ? JSON.stringify(currentContent) : null,
        sectionsJson: Object.keys(currentSections).length > 0 ? JSON.stringify(sectionsToApiFormat(currentSections)) : null,
        appearanceJson: Object.keys(currentAppearance).length > 0 ? JSON.stringify(currentAppearance) : null,
        version: draftVersionRef.current,
      };

      const res = await api.put(apiRoutes.SiteDraft, payload, {
        headers: getAdminAuthHeaders(),
      });

      const saved = res?.data?.payload?.draft;
      if (saved) {
        setDraftVersion(saved.version);
        draftVersionRef.current = saved.version;
        setDraftMeta({
          createdAt: saved.createdAt,
          updatedAt: saved.updatedAt,
          createdByUserId: saved.createdByUserId,
          updatedByUserId: saved.updatedByUserId,
        });
        setIsDirty(false);
        setLastSaveError(null);
        return { success: true, version: saved.version };
      }
      // API returned 200 but no draft payload — treat as success with current version
      setIsDirty(false);
      setLastSaveError(null);
      return { success: true, version: draftVersionRef.current };
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Unknown save error';
      console.error('[DraftContext] Failed to save draft to API:', msg);
      setLastSaveError(msg);
      return { success: false, reason: 'save-failed' };
    } finally {
      isSavingRef.current = false;
      saveInProgressPromiseRef.current = null;
      setIsSaving(false);
    }
    })();
    saveInProgressPromiseRef.current = savePromise;
    return savePromise;
  }, [draftContent, draftSections, draftAppearance, draftVersion]);

  // Auto-save to API whenever draft data changes (debounced 1.5s)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!isDirty || !isAdmin || isPublishingRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const result = await saveDraft();
      try {
        if (result.success) {
          window.dispatchEvent(new CustomEvent('draftSaved', { detail: { version: result.version } }));
        } else if (result.reason === 'save-failed') {
          window.dispatchEvent(new CustomEvent('draftSaveError', { detail: { error: lastSaveError || 'Draft save failed' } }));
        }
        // For 'already-saving', 'discarding', 'publishing' — silent, another save is in flight
      } catch { /* event dispatch errors are non-critical */ }
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [isDirty, draftContent, draftSections, draftAppearance]);

  // --- Publish (server-side: POST publish endpoint) ---
  const publishDraft = useCallback(async () => {
    // Gate: require a valid admin token for the current org before attempting publish.
    // If missing/expired and no refresh token, signal admin-session-expired.
    const adminToken = getAuthorizedContentAdminToken(tlfId);
    if (!adminToken && !getAdminRefreshToken()) {
      const journal = {
        content: 'failed', sections: 'failed', appearance: 'failed',
        errors: { publish: 'Admin session expired. Please sign in again.' },
        adminSessionExpired: true,
      };
      setPublishStatus(journal);
      return journal;
    }

    // Prevent auto-save from racing during publish
    isPublishingRef.current = true;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    // Flush any pending saves first and capture the latest version.
    // Also flush when a save is already in-flight (auto-save fired just before
    // publish was clicked) — awaiting the in-flight promise is safe because
    // saveDraft() returns the same promise for concurrent callers.
    let versionToPublish = draftVersionRef.current;
    if (isDirty || isSavingRef.current) {
      const saveResult = await saveDraft();
      if (!saveResult.success) {
        const reason = saveResult.reason === 'save-failed'
          ? `Draft save failed: ${lastSaveError || 'unknown error'}. Nothing was published; retry after the draft saves successfully.`
          : 'Draft save could not complete. Nothing was published; retry.';
        const journal = {
          content: 'failed',
          sections: 'failed',
          appearance: 'failed',
          errors: { publish: reason },
        };
        setPublishStatus(journal);
        isPublishingRef.current = false;
        return journal;
      }
      // Always read from the ref after any save — it may have been updated
      // by the in-flight auto-save we awaited rather than a fresh save.
      versionToPublish = draftVersionRef.current;
    }

    setIsPublishing(true);
    setPublishStatus({ content: 'pending', sections: 'pending', appearance: 'pending', errors: {} });

    try {
      const res = await api.post(apiRoutes.SiteDraftPublish, { version: versionToPublish }, {
        headers: getAdminAuthHeaders(),
      });

      const result = res?.data?.payload;

      // Detect domain-level failures even when HTTP status is 200 and isError is falsy
      const journal = {
        content: result?.contentStatus || 'skipped',
        sections: result?.sectionsStatus || 'skipped',
        appearance: result?.appearanceStatus || 'skipped',
        errors: {},
      };
      if (result?.contentError) journal.errors.content = result.contentError;
      if (result?.sectionsError) journal.errors.sections = result.sectionsError;
      if (result?.appearanceError) journal.errors.appearance = result.appearanceError;
      // Also capture top-level API error (isError / message) as a publish error
      if (res?.data?.isError && res?.data?.message) {
        journal.errors.publish = res.data.message;
      }

      setPublishStatus(journal);

      const allSuccess =
        journal.content !== 'failed' &&
        journal.sections !== 'failed' &&
        journal.appearance !== 'failed' &&
        !journal.errors.publish;

      if (allSuccess) {
        // Update SITE_CONFIG in-memory with the just-published appearance so
        // that context resets (below) read the NEW values, not the stale ones.
        const publishedAppearance = { ...draftAppearance };
        if (window.SITE_CONFIG) {
          if (!window.SITE_CONFIG.defaults) window.SITE_CONFIG.defaults = {};
          if (publishedAppearance.layoutStyle) {
            window.SITE_CONFIG.defaults.layout_style = publishedAppearance.layoutStyle;
          }
          if (publishedAppearance.colorTheme) {
            window.SITE_CONFIG.defaults.color_theme = publishedAppearance.colorTheme;
          }
          if (publishedAppearance.countdownStyle) {
            window.SITE_CONFIG.defaults.countdown_style = publishedAppearance.countdownStyle;
          }
          if (publishedAppearance.jackpotStyle) {
            window.SITE_CONFIG.defaults.jackpot_style = publishedAppearance.jackpotStyle;
          }
          if (publishedAppearance.jackpotAnimationStyle) {
            window.SITE_CONFIG.defaults.jackpot_style = publishedAppearance.jackpotAnimationStyle;
          }
          if (publishedAppearance.gridLayout) {
            window.SITE_CONFIG.defaults.grid_layout = publishedAppearance.gridLayout;
          }
          if (publishedAppearance.bgLight) {
            window.SITE_CONFIG.defaults.bg_light = publishedAppearance.bgLight;
          }
          if (publishedAppearance.bgDark) {
            window.SITE_CONFIG.defaults.bg_dark = publishedAppearance.bgDark;
          }
          if (publishedAppearance.navBg) {
            window.SITE_CONFIG.defaults.nav_bg = publishedAppearance.navBg;
          }
          if (publishedAppearance.navText) {
            window.SITE_CONFIG.defaults.nav_text = publishedAppearance.navText;
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'defaultPageVisibility')) {
            window.SITE_CONFIG.defaults.default_page_visibility = publishedAppearance.defaultPageVisibility;
            window.SITE_CONFIG.defaults.defaultPageVisibility = publishedAppearance.defaultPageVisibility;
            try {
              localStorage.setItem('site_default_page_visibility', JSON.stringify(publishedAppearance.defaultPageVisibility));
            } catch {}
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'defaultDarkMode')) {
            window.SITE_CONFIG.defaults.default_dark_mode = !!publishedAppearance.defaultDarkMode;
            window.dispatchEvent(new CustomEvent('orgDarkModeDefault', { detail: !!publishedAppearance.defaultDarkMode }));
          }
          if (publishedAppearance.partnerPageDisplayStyle) {
            window.SITE_CONFIG.defaults.partner_page_display_style = publishedAppearance.partnerPageDisplayStyle;
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'partnerPageShowPlacement')) {
            window.SITE_CONFIG.defaults.partner_page_show_placement = publishedAppearance.partnerPageShowPlacement;
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'partnerPageShowSupporters')) {
            window.SITE_CONFIG.defaults.partner_page_show_supporters = publishedAppearance.partnerPageShowSupporters;
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'partnerPageShowTickets')) {
            window.SITE_CONFIG.defaults.partner_page_show_tickets = publishedAppearance.partnerPageShowTickets;
          }
          if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'partnerPageShowAmount')) {
            window.SITE_CONFIG.defaults.partner_page_show_amount = publishedAppearance.partnerPageShowAmount;
          }
          if (publishedAppearance.partnerPageDefaultSort) {
            window.SITE_CONFIG.defaults.partner_page_default_sort = publishedAppearance.partnerPageDefaultSort;
          }
          if (publishedAppearance.aboutPageLayout) {
            window.SITE_CONFIG.defaults.about_page_layout = publishedAppearance.aboutPageLayout;
            try {
              localStorage.setItem('site_about_page_layout', publishedAppearance.aboutPageLayout);
            } catch {}
          }
          if (publishedAppearance.how_to_playPageLayout) {
            window.SITE_CONFIG.defaults.how_to_play_page_layout = publishedAppearance.how_to_playPageLayout;
            try {
              localStorage.setItem('site_how_to_play_page_layout', publishedAppearance.how_to_playPageLayout);
            } catch {}
          }
          if (publishedAppearance.winnersPageLayout) {
            window.SITE_CONFIG.defaults.winners_page_layout = publishedAppearance.winnersPageLayout;
            try {
              localStorage.setItem('site_winners_page_layout', publishedAppearance.winnersPageLayout);
            } catch {}
          }
          if (publishedAppearance.drawsPageLayout) {
            window.SITE_CONFIG.defaults.draws_page_layout = publishedAppearance.drawsPageLayout;
            try {
              localStorage.setItem('site_draws_page_layout', publishedAppearance.drawsPageLayout);
            } catch {}
          }
          // Directly apply CSS custom properties so they survive the
          // React re-render cycle during publish (the panel's useEffect
          // may briefly reset to defaults before the event handler fires).
          const root = document.documentElement;
          if (publishedAppearance.bgLight) {
            root.style.setProperty('--app-bg-light', publishedAppearance.bgLight);
          }
          if (publishedAppearance.bgDark) {
            root.style.setProperty('--app-bg-dark', publishedAppearance.bgDark);
          }
          if (publishedAppearance.navBg) {
            root.style.setProperty('--nav-bg', publishedAppearance.navBg);
          }
          if (publishedAppearance.navText) {
            root.style.setProperty('--nav-text', publishedAppearance.navText);
          }
        }

        // Publish goal settings to the OrganizationGoal API if present in draft
        if (Object.prototype.hasOwnProperty.call(publishedAppearance, 'goalEnabled')) {
          try {
            const { tlfId } = getConfig();
            await api.put(apiRoutes.AdminOrganizationGoal, {
              organizationId: tlfId,
              goalAmount: Number(publishedAppearance.goalAmount) || 0,
              theme: publishedAppearance.goalTheme || 'classic-red',
              title: publishedAppearance.goalTitle || '',
              isEnabled: !!publishedAppearance.goalEnabled,
            }, { headers: getAdminAuthHeaders() });
          } catch (e) {
            console.warn('[DraftContext] Failed to publish goal settings:', e?.message);
          }
        }

        // Capture published content BEFORE clearing state so ContentContext can
        // merge it directly into the live content store, bypassing the
        // appearsTranslated filter that would otherwise strip non-accented values.
        const publishedContent = { ...draftContent };

        // Draft deleted server-side; clear local state
        draftContentRef.current = {};
        draftSectionsRef.current = {};
        draftAppearanceRef.current = {};
        setDraftContent({});
        setDraftSections({});
        setDraftAppearance({});
        setDraftVersion(0);
        draftVersionRef.current = 0;
        setDraftMeta(null);
        setIsDirty(false);
        // Clear live section cache so it reloads from API
        try { localStorage.removeItem('poc_page_sections'); } catch {}
        // Reset appearance contexts to (now-updated) server values
        try {
          localStorage.removeItem('jackpotStyle');
          localStorage.removeItem('gridLayout');
        } catch {}
        window.dispatchEvent(new CustomEvent('appearanceReset'));
        // Broadcast publish completion — include the published content so
        // ContentContext can apply it immediately without waiting for re-fetch.
        window.dispatchEvent(new CustomEvent('draftPublished', { detail: { publishedContent } }));
      } else {
        // Reload the draft (server kept it for retry)
        try {
          const reloadRes = await api.get(apiRoutes.SiteDraft, { headers: getAdminAuthHeaders() });
          const draft = reloadRes?.data?.payload?.draft;
          if (draft) {
            const parsedContent = draft.contentJson ? JSON.parse(draft.contentJson) : {};
            const parsedSections = draft.sectionsJson ? sectionsFromApiFormat(JSON.parse(draft.sectionsJson)) : {};
            const parsedAppearance = draft.appearanceJson ? JSON.parse(draft.appearanceJson) : {};
            draftContentRef.current = parsedContent;
            draftSectionsRef.current = parsedSections;
            draftAppearanceRef.current = parsedAppearance;
            setDraftContent(parsedContent);
            setDraftSections(parsedSections);
            setDraftAppearance(parsedAppearance);
            setDraftVersion(draft.version || 0);
            draftVersionRef.current = draft.version || 0;
          }
        } catch {}
      }

      setIsPublishing(false);
      isPublishingRef.current = false;
      return journal;
    } catch (err) {
      const isAuthError = err.response?.status === 401;
      const journal = {
        content: 'failed', sections: 'failed', appearance: 'failed',
        errors: { publish: err.message || 'Publish request failed' },
        adminSessionExpired: isAuthError,
      };
      setPublishStatus(journal);
      setIsPublishing(false);
      isPublishingRef.current = false;
      return journal;
    }
  }, [draftVersion, isDirty, saveDraft]);

  // --- Discard draft (DELETE via API) ---
  const discardDraft = useCallback(async () => {
    // Prevent any pending/in-flight auto-saves from re-creating the draft
    isDiscardingRef.current = true;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    // Capture version before clearing state (needed for DELETE)
    const versionToDelete = draftVersionRef.current;

    // Attempt server-side delete FIRST before clearing local state
    try {
      await api.delete(`${apiRoutes.SiteDraft}?version=${versionToDelete}`, {
        headers: getAdminAuthHeaders(),
      });
    } catch (err) {
      console.warn('[DraftContext] Failed to delete draft from API:', err.message);
      // Server still has the draft — don't clear local state (would cause ghost revert)
      isDiscardingRef.current = false;
      return;
    }

    // DELETE succeeded — now safe to clear local state
    draftContentRef.current = {};
    draftSectionsRef.current = {};
    draftAppearanceRef.current = {};
    setDraftContent({});
    setDraftSections({});
    setDraftAppearance({});
    setDraftVersion(0);
    draftVersionRef.current = 0;
    setDraftMeta(null);
    setPublishStatus(null);
    setIsDirty(false);

    // Reset appearance contexts to server values
    try {
      localStorage.removeItem('jackpotStyle');
      localStorage.removeItem('gridLayout');
    } catch {}
    window.dispatchEvent(new CustomEvent('appearanceReset'));
    // Broadcast discard so pages reload published data
    window.dispatchEvent(new CustomEvent('draftDiscarded'));

    isDiscardingRef.current = false;
  }, [draftVersion]);

  // Build change summary for publish modal
  const getChangeSummary = useCallback(() => {
    const summary = { content: [], sections: [], appearance: [] };
    for (const [lang, entries] of Object.entries(draftContent || {})) {
      if (!entries || typeof entries !== 'object') continue;
      for (const key of Object.keys(entries)) {
        summary.content.push({ key, lang });
      }
    }
    for (const [pageName, sections] of Object.entries(draftSections || {})) {
      summary.sections.push({ pageName, count: Array.isArray(sections) ? sections.length : (sections?.length || 0) });
    }
    for (const [field, value] of Object.entries(draftAppearance || {})) {
      summary.appearance.push({ field, value });
    }
    return summary;
  }, [draftContent, draftSections, draftAppearance]);

  const value = {
    // State
    draftContent,
    draftSections,
    draftAppearance,
    draftMeta,
    draftVersion,
    hasDraft,
    changeCount,
    contentChangeCount,
    sectionChangeCount,
    appearanceChangeCount,
    isPreviewMode,
    shouldShowDraft,
    publishStatus,
    isPublishing,
    isSaving,
    isDirty,
    isLoaded,
    lastSaveError,

    // Content
    updateDraftContent,
    getDraftContent,

    // Sections
    updateDraftSections,
    getDraftSections,

    // Appearance
    updateDraftAppearance,
    getDraftAppearance,

    // Actions
    saveDraft,
    publishDraft,
    discardDraft,
    setIsPreviewMode,
    getChangeSummary,
  };

  return (
    <DraftContext.Provider value={value}>
      {children}
    </DraftContext.Provider>
  );
}

export function useDraft() {
  const ctx = useContext(DraftContext);
  if (!ctx) throw new Error('useDraft must be used within a DraftProvider');
  return ctx;
}

export function useOptionalDraft() {
  return useContext(DraftContext);
}
